import React from 'react';
import styles from '../CreateProductFlow.module.scss';
import {
  ButtonLabels,
  FieldProps,
  RawMaterialProps,
} from 'enums/RawMaterialEnums';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import clsx from 'clsx';
import IngridientNameField from '../IngridientNameField';
import { ReactComponent as Add } from 'assets/icons/add.svg';
import InputField from 'shared/components/InputField';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import { ReactComponent as MapSearch } from 'assets/icons/map-search.svg';
import BestFitRegion from '../BestFitRegion';
import COSelect from 'shared/components/COSelect';
import { FC } from 'react';
import { ArrayHelpers, FormikProps, useFormikContext } from 'formik';
import {
  Origin,
  rawMaterialPayload,
} from 'views/CreateProductFlow/CreateProductFlowInterfaces';
import { unitOptions } from '../CreateProductFlowConstants';
import { Product } from 'models/Product/product.model';
import hasErrorAndTouched from 'shared/utils/hasErrorAndTouched';
import { RawMaterialPackagingMeta } from 'models/RawMaterialPackagingMeta/rawMaterialPackagingMeta.model';
import { Col, Tooltip, Typography } from 'antd';
import COButton from 'shared/components/COButton';
import PackagingMaterialOriginFields from './PackagingMaterialOriginFields';
import {
  handlePackagingOnChange,
  onOriginClick,
} from './PackagingMaterialUtils';

interface PackagingMaterialListInterface {
  rawMateialPackagingMeta: RawMaterialPackagingMeta[];
  handleOpenRequestPackageMaterialModal: () => void;
  isDisabled: boolean;
  arrayHelpers: ArrayHelpers;
  fetchData: (params?: { 'input-field': string }) => void;
  showLocationModal: (
    index: number,
    type: string,
    selectedIndexData?: Origin[],
    selectedRecordIndex?: number,
    selectedRecordType?: string,
    selectedOriginIndex?: number
  ) => void;
  newProduct?: Product;
  handleAddOrigins: (
    formikProps: FormikProps<rawMaterialPayload>,
    index: number,
    type?: string,
    id?: string
  ) => void;
  handleRemoveOrigins: (
    formikProps: FormikProps<rawMaterialPayload>,
    index: number,
    originIndex: number,
    type?: string
  ) => void;
}

const PackagingMaterialList: FC<PackagingMaterialListInterface> = props => {
  const formikProps = useFormikContext<rawMaterialPayload>();

  const {
    rawMateialPackagingMeta,
    handleOpenRequestPackageMaterialModal,
    isDisabled,
    arrayHelpers,
    showLocationModal,
    fetchData,
    newProduct,
    handleAddOrigins,
    handleRemoveOrigins,
  } = props;

  const handlePackagingOnChangeWrapper = (value: string, index: number) => {
    handlePackagingOnChange(value, index, formikProps);
  };

  const onOriginClickWrapper = (index: number) => {
    onOriginClick(index, isDisabled, formikProps, showLocationModal);
  };

  return (
    <div className={styles.ingredientsFormContainer}>
      <h2>{RawMaterialProps.PACKAGE_MATERIAL_HEADING}</h2>
      {formikProps.values.packageMaterials.map((_, index: number) => (
        <React.Fragment key={`${RawMaterialProps.PACKAGING}-${index}`}>
          <div>
            <div
              key={index}
              className={clsx({
                [styles.fieldRow]: true,
                [styles.packagingContainer]: true,
              })}>
              <div>
                <p className={styles.pointer}>{index + 1}</p>
              </div>
              <div className={styles.ingredientFieldContainer}>
                <IngridientNameField
                  label={FieldProps.PACKAGING_NAME_PLACEHOLDER}
                  placeholder={FieldProps.PACKAGING_NAME_PLACEHOLDER}
                  onChange={value =>
                    handlePackagingOnChangeWrapper(value, index)
                  }
                  options={rawMateialPackagingMeta}
                  dropdownRender={menu => {
                    return (
                      <>
                        <div
                          className={styles.dropdownCreateButton}
                          onClick={handleOpenRequestPackageMaterialModal}>
                          <Add /> {FieldProps.REQUEST_PACKAGING_MATERIAL}
                        </div>
                        {menu}
                      </>
                    );
                  }}
                  disabled={isDisabled ? true : false}
                  name={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_NAME}`}
                  bestFitRegionName={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_REGION}`}
                  ingredientOriginName={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_ORIGIN}`}
                  addressName={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.ADDRESS}`}
                />
              </div>
              <div className={styles.bigFieldContainer}>
                <InputField
                  key={FieldProps.PACKAGING_ORIGIN}
                  disabled={
                    isDisabled ||
                    formikProps.values.packageMaterials[index].origins?.length
                      ? true
                      : false
                  }
                  label={FieldProps.PACKAGING_ORIGIN_PLACEHOLDER}
                  name={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_ORIGIN}`}
                  placeholderstring={FieldProps.PACKAGING_ORIGIN_PLACEHOLDER}
                  onFocus={() => onOriginClickWrapper(index)}
                  suffix={
                    true ? (
                      <span onClick={() => {}}>
                        <MapSearch />
                      </span>
                    ) : undefined
                  }
                />
              </div>
              <div className={styles.bigFieldContainer}>
                <BestFitRegion
                  label={FieldProps.PACKAGING_REGION_PLACEHOLDER}
                  placeholder={FieldProps.PACKAGING_REGION_PLACEHOLDER}
                  disabled={
                    isDisabled ||
                    formikProps.values.packageMaterials[index].origins?.length
                      ? true
                      : false
                  }
                  onFocus={() => {
                    fetchData({
                      'input-field': 'packaging material production region',
                    });
                  }}
                  name={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_REGION}`}
                  ingridientFieldName={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_NAME}`}
                  isIngredientId={false}
                  errorMessageStyle={styles.errorMessage}
                />
              </div>

              <div className={styles.smallFieldContainer}>
                <InputField
                  key={FieldProps.PACKAGING_QUANTITY}
                  disabled={isDisabled ? true : false}
                  name={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_QUANTITY}`}
                  label={FieldProps.PACKAGING_QUANTITY_PLACEHOLDER}
                  onFocus={() => {
                    fetchData({
                      'input-field': 'packaging material weight',
                    });
                  }}
                  placeholderstring={FieldProps.PACKAGING_QUANTITY_PLACEHOLDER}
                />
              </div>
              <div className={styles.smallFieldContainer}>
                <COSelect
                  name={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_UNIT}`}
                  label={FieldProps.PACKAGING_UNIT_PLACEHOLDER}
                  disabled={isDisabled ? true : false}
                  placeholderstring={FieldProps.PACKAGING_UNIT_PLACEHOLDER}
                  options={unitOptions}
                  onFocus={() => {
                    fetchData({
                      'input-field': 'packaging material weight',
                    });
                  }}
                />
              </div>
              <div className={styles.smallFieldContainer}>
                <InputField
                  key={FieldProps.PACKAGING_UNITS_PER_PACKAGING}
                  disabled={isDisabled ? true : false}
                  name={`${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_UNITS_PER_PACKAGING}`}
                  label={FieldProps.PACKAGING_UNITS_PER_PACKAGING_PLACEHOLDER}
                  placeholderstring={
                    FieldProps.PACKAGING_UNITS_PER_PACKAGING_PLACEHOLDER
                  }
                />
              </div>
              <h3>
                {ButtonLabels.ROOT}
                {newProduct?.netWeight} {newProduct?.netWeightUnit}
              </h3>
              {!isDisabled && (
                <div
                  className={styles.addIconContainer}
                  onClick={() =>
                    handleAddOrigins(
                      formikProps,
                      index,
                      RawMaterialProps.PACKAGE_MATERIAL,
                      `${RawMaterialProps.PACKAGE_MATERIAL}-${RawMaterialProps.ADD_ORIGIN_BUTTON}-${index}`
                    )
                  }>
                  <Tooltip title={'Add origin'}>
                    <Typography.Paragraph>
                      <AddIcon />
                    </Typography.Paragraph>
                  </Tooltip>
                </div>
              )}

              {isDisabled === false && (
                <div
                  className={styles.deleteButton}
                  onClick={() => arrayHelpers.remove(index)}>
                  <DeleteButton
                    className={clsx(
                      hasErrorAndTouched(
                        formikProps.errors.packageMaterials?.[index],
                        formikProps.touched.packageMaterials?.[index],
                        styles
                      )
                    )}
                  />
                </div>
              )}
            </div>
          </div>
          {/* multiple origin fields for packaging material */}
          {(formikProps.values.packageMaterials[index].origins || []).map(
            (_, originIndex) => (
              <PackagingMaterialOriginFields
                key={`${RawMaterialProps.MULTIPLE_ORIGIN}-${index}-${originIndex}`}
                index={index}
                originIndex={originIndex}
                isDisabled={isDisabled}
                formikProps={formikProps}
                fetchData={fetchData}
                showLocationModal={showLocationModal}
                handleRemoveOrigins={handleRemoveOrigins}
              />
            )
          )}
          {(formikProps.values.packageMaterials[index]?.origins ?? []).length >=
            1 &&
            !isDisabled && (
              <div
                className={styles.buttonFieldRow}
                key={`${RawMaterialProps.PACKAGE_MATERIAL}-${RawMaterialProps.ADD_ORIGIN_BUTTON}-${index}`}
                id={`${RawMaterialProps.PACKAGE_MATERIAL}-${RawMaterialProps.ADD_ORIGIN_BUTTON}-${index}`}>
                <div className={styles.numberEmptyContainer}></div>
                <div className={styles.ingredientFieldContainer}></div>
                <Col>
                  <COButton
                    text
                    disabled={isDisabled}
                    onClick={() =>
                      handleAddOrigins(
                        formikProps,
                        index,
                        RawMaterialProps.PACKAGE_MATERIAL,
                        `${RawMaterialProps.PACKAGE_MATERIAL}-${RawMaterialProps.ADD_ORIGIN_BUTTON}-${index}`
                      )
                    }>
                    <AddIcon />
                    {ButtonLabels.ADD_ORIGIN}
                  </COButton>
                </Col>
              </div>
            )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default PackagingMaterialList;
